/**
 * Single source of truth for agent-type display metadata.
 *
 * Backend stores raw type/flag data on the Agent row (see Agent.java —
 * `type` string column, plus `isExternal` and `isSystemAgent` boolean
 * flags, and a name-prefix convention `AutoLets-...` for auto-created
 * agents). This module classifies an Agent into one of four mutually-
 * exclusive UI buckets, each with its own brand label and color.
 *
 * Naming (2026-05-04 rename):
 *   • AutoLets  — agents auto-created by the Auto Create runbook wizard.
 *   • AgentLets — handcrafted native Agentlets (note the capital L).
 *   • ExteLets  — external agents the platform observes via the
 *                 GEL-enforced bridge.
 *   • SysLets   — platform-internal system agents.
 *
 * Palette — a deliberate three-warm + one-cool composition that lets
 * the buyer read agent provenance at a glance:
 *
 *   • AutoLets   — Vivid Red-Orange #FF5722  (the spark — auto-creation
 *                                            energy; brightest in the
 *                                            warm family).
 *   • AgentLets  — Brand Red #E24A3A         (the hero — canonical
 *                                            Agentlets identity, most
 *                                            saturated red-anchor).
 *   • ExteLets   — Sky Blue #0EA5E9          (the imported — only cool
 *                                            colour, semantically
 *                                            signals "from outside our
 *                                            world, governed via GEL").
 *   • SysLets    — Warm Graphite #44403C    (the bedrock — settled,
 *                                            foundational, warm-leaning
 *                                            neutral that ties back to
 *                                            the brand greys).
 *
 * The blue isn't a stylistic break — it's semantic. Three warm colours
 * (tangerine + red + iron) say "these agents belong to Agentlets". The
 * one cool sky blue says "this agent comes from outside the platform
 * and is observed via the GEL bridge". A buyer scanning the Overview
 * grid can tell native vs external apart without reading a single
 * label.
 *
 * Backgrounds use the lightest tint of each accent so card surfaces
 * never break the cohesion — even the ExteLets sky-50 reads as a soft
 * tint rather than a cold panel against the brand cream.
 *
 * Tested for the security/compliance/risk buyer: polished, serious,
 * warm-dominant with one disciplined cool to mark "external" — earth
 * tones plus a single sky note, no candy colours, no neon.
 *
 * Every UI surface (filter chips, card borders, type badges, dropdown
 * group headings, page titles) imports this module so a future tweak
 * to a label or hex value lands in exactly one place.
 */

export const AGENT_TYPES = {
  autolets: {
    id: 'autolets',
    label: 'AutoLets',
    longLabel: 'Auto-created AgentLets',
    description: 'Agents generated by the Auto Create runbook pipeline.',
    // Vivid red-orange. Brighter and redder than tangerine — pushes
    // toward the brand red so AutoLets reads as "the high-energy
    // sibling of AgentLets". Saturated enough to grab attention as
    // the newest agents in a fleet but warm enough to belong.
    accent:     '#FF5722',
    accentText: '#9A2A0F',
    border:     '#FFAB91',
    bg:         '#FFF3EE',
    bgGradient: 'linear-gradient(135deg, #FFF3EE 0%, #FFFFFF 60%)',
    order: 1,
  },
  agentlet: {
    id: 'agentlet',
    // Naming convention (2026-05-04): the four agent types form a
    // family with the "-Lets" suffix — AutoLets, ExteLets, SysLets —
    // and the canonical handcrafted variant is the unmodified BASE
    // form, just "Lets". Reads as "Lets are the handcrafted ones;
    // every other Lets is a derivative." Internal key stays
    // `agentlet` so existing sessionStorage / route params resolve.
    label: 'Lets',
    longLabel: 'Handcrafted Lets',
    description: 'Handcrafted native agents authored in the platform.',
    // Brand red — the canonical Agentlets identity. Most saturated of
    // the four because AgentLets is the hero product; this is what
    // the buyer came for, so it should anchor the palette.
    accent:     '#E24A3A',
    accentText: '#7A1F12',
    border:     '#F5C4BE',
    bg:         '#FEF2F0',
    bgGradient: 'linear-gradient(135deg, #FEF2F0 0%, #FFFFFF 60%)',
    order: 2,
  },
  // Internal key stays `external` so sessionStorage values from before
  // the rename still resolve cleanly. Display label is "ExteLets".
  external: {
    id: 'external',
    label: 'ExteLets',
    longLabel: 'External GEL-enforced agents',
    description: 'External agents the platform observes via the GEL bridge.',
    // Deep maroon-blue-black (indigo-950). The bright blue-700 used
    // earlier read as "tech blue" and competed for visual weight with
    // the brand red; this near-black indigo carries both navy and
    // subtle aubergine/maroon undertones, so it pairs as a
    // complementary deep tone with #E24A3A rather than a competing
    // accent. Reads as "weighty, governed, formal" — the right register
    // for external agents being policed via the GEL bridge.
    // Border + bg use the lightest indigo tints so cards stay airy
    // even when the accent is this dark.
    accent:     '#1E1B4B',
    accentText: '#1E1B4B',
    border:     '#C7D2FE',
    bg:         '#EEF2FF',
    bgGradient: 'linear-gradient(135deg, #EEF2FF 0%, #FFFFFF 60%)',
    order: 3,
  },
  system: {
    id: 'system',
    label: 'SysLets',
    longLabel: 'Agentlets System Agents',
    description: 'Platform-internal agents that power Agentlets itself.',
    // Warm graphite. The "settled" colour — system agents are platform
    // infrastructure and should never compete for attention. Note: this
    // is a WARM grey (stone-700, undertones lean orange) not a cool
    // grey, so it stays in the brand family rather than reading as a
    // foreign neutral.
    accent:     '#44403C',
    accentText: '#1C1917',
    border:     '#D6D3D1',
    bg:         '#FAFAF9',
    bgGradient: 'linear-gradient(135deg, #FAFAF9 0%, #FFFFFF 60%)',
    order: 4,
  },
};

/** Iteration order for filter chips, dropdown groups, etc. */
export const AGENT_TYPE_ORDER = ['autolets', 'agentlet', 'external', 'system'];

/**
 * Classify an Agent row into one of the four UI buckets.
 *
 * <h3>Phase 4 — Prefer the backend-computed `archetype` field</h3>
 * <p>The Agent entity (V178 + Phase 4) exposes a computed
 * `archetype` property returning `Let` | `AutoLet` | `ExteLet` |
 * `SysLet`. That's the source of truth — the same logic the
 * `agent_archetype()` SQL function uses for SQL consumers. We
 * map those values to the four UI bucket keys.</p>
 *
 * <p>The legacy name-prefix fallback (matching `AutoLets-` prefix)
 * is REMOVED — it broke any time an AutoLet was renamed. The
 * deeper hierarchy of flag-based fallbacks is retained so older
 * agent JSON payloads (pre-Phase-4) still classify correctly:</p>
 *
 * <ol>
 *   <li>If <code>agent.archetype</code> is present, trust it directly.</li>
 *   <li>Else fall back to flag inspection
 *       (<code>isSystemAgent</code> / <code>isExternal</code> /
 *       <code>isRunbookManaged</code>).</li>
 * </ol>
 *
 * @param {Object} agent — Agent JSON payload from the backend.
 * @returns {keyof AGENT_TYPES}
 */
export function classifyAgentType(agent) {
  if (!agent) return 'agentlet';

  // Phase 4 — Prefer the server-computed archetype field. This is
  // the V178 SQL function's output, surfaced on every Agent JSON
  // response via Agent.getArchetype() (computed @Transient getter).
  // Renaming an agent no longer changes its bucket.
  const archetype = agent.archetype;
  if (archetype === 'SysLet')  return 'system';
  if (archetype === 'ExteLet') return 'external';
  if (archetype === 'AutoLet') return 'autolets';
  if (archetype === 'Let')     return 'agentlet';

  // Backwards-compat fallback for any cached / pre-Phase-4 payload
  // that doesn't carry an `archetype` field. Read straight from the
  // flag columns — same precedence the backend getter uses.
  if (agent.isSystemAgent || agent.category === 'system' || agent.type === 'system') {
    return 'system';
  }
  if (agent.isExternal
      || agent.agentClassification === 'EXTERNAL'
      || agent.agentClassification === 'EXTERNAL_API'
      || agent.agentClassification === 'EXTERNAL_SDK') {
    return 'external';
  }
  if (agent.isRunbookManaged) return 'autolets';
  return 'agentlet';
}

/** Convenience accessor — null-safe lookup with fallback. */
export function agentTypeMeta(typeKey) {
  return AGENT_TYPES[typeKey] || AGENT_TYPES.agentlet;
}
